/*
 * ── Sidebar Filtering Engine ──
 *
 * Takes the unified SIDEBAR_MENU config + user context and returns
 * the exact set of menu items that should be rendered.
 *
 * This replaces the old pattern of 3 hardcoded arrays + conditional pushes.
 * All visibility logic lives here — Sidebar.jsx just renders what it receives.
 *
 * ─── WORKSPACE RESOLUTION ───
 *
 *   workspace is a frontend-only concept that determines which "mode"
 *   the sidebar renders in. Currently derived from the user's role/flags.
 *   In the future, the RoleSwitcher can pass a workspace override.
 *
 *   company  → Manager, company owner, planner, team member (default)
 *   staff    → Staff-only user (role === 'staff')
 *   vendor   → Vendor user (isVendor flag or industryType === 'vendor')
 *
 * ─── EXTENDING ───
 *
 *   New workspace:   Add a case to resolveWorkspace().
 *   New role:        Tag items in sidebarMenu.js, filtering handles the rest.
 *   Feature flags:   Check item.featureFlag against a flags object here.
 *   RoleSwitcher:    Pass workspaceOverride to getVisibleSidebar().
 */

import { WORKSPACE_SECTION_ORDER } from '../config/sidebarMenu';
import { SHOW_EXPERIMENTAL_FEATURES } from './featureFlags';

// Legacy env FLAG_MAP retained for reference only. Per-tenant entitlement
// flags now ride on user.entitlements and are checked directly below.
const FLAG_MAP = {};
void FLAG_MAP;

/**
 * Check if a user is the STAFFED platform owner (not a tenant admin).
 * Authority is decided ONCE by the backend (platform_owner.py) and delivered
 * as `isPlatformOwner` on the user. No email strings live in the frontend.
 */
export function isPlatformOwner(user) {
  return user?.isPlatformOwner === true;
}

/**
 * Determine the current workspace from user data.
 * This is the ONLY place workspace resolution logic lives.
 *
 * Workspace is a frontend-only concept — it does NOT change backend role.
 *
 *   staff   → staff-only users (role === 'staff')
 *   vendor  → vendor users (isVendor flag or industryType === 'vendor')
 *   planner → event planner users (industryType === 'event_planner')
 *   company → DJ companies, managers, admins, team members (default)
 *
 * @param {Object} user — user object from auth context
 * @returns {'company'|'staff'|'vendor'|'planner'} current workspace
 */
export function resolveWorkspace(user) {
  if (user?.role === 'staff') return 'staff';
  if (user?.isVendor || user?.industryType === 'vendor') return 'vendor';
  if (user?.industryType === 'event_planner') return 'planner';
  // dj_company managers, company_owner, admin, member — company workspace
  return 'company';
}


/**
 * Check if a user qualifies as a platform admin.
 *
 * @param {Object} user
 * @returns {boolean}
 */
function isAdminUser(user) {
  return user?.isPlatformOwner === true;
}


/**
 * Check if a single menu item (or child) passes all visibility rules.
 *
 * @param {Object} item — menu item from config
 * @param {Object} user — user object
 * @param {string} workspace — resolved workspace
 * @param {boolean} isAdmin — pre-computed admin flag
 * @returns {boolean}
 */
function isItemVisible(item, user, workspace, isAdmin) {
  const userRole = user?.role || '';
  const userIndustry = user?.industryType || '';

  // 0. Experimental / off-path gate — hidden unless SHOW_EXPERIMENTAL_FEATURES.
  if (item.experimental && !SHOW_EXPERIMENTAL_FEATURES) {
    return false;
  }

  // 1. Workspace gate — most common filter
  if (item.allowedWorkspaces && !item.allowedWorkspaces.includes(workspace)) {
    return false;
  }

  // 2. Admin-only gate
  if (item.adminOnly && !isAdmin) {
    return false;
  }

  // 2a. Platform-owner-only gate (STAFFED system, not tenant admins)
  if (item.platformOwnerOnly && !isPlatformOwner(user)) {
    return false;
  }

  // 3. Role allow-list (if specified, user must have one of these roles)
  if (item.allowedRoles && !item.allowedRoles.includes(userRole)) {
    return false;
  }

  // 4. Role deny-list (hide from specific roles, e.g. 'member')
  if (item.hideForRoles && item.hideForRoles.includes(userRole)) {
    return false;
  }

  // 5. Industry allow-list
  if (item.allowedIndustryTypes && !item.allowedIndustryTypes.includes(userIndustry)) {
    return false;
  }

  // 6. Industry deny-list
  if (item.hideForIndustryTypes && item.hideForIndustryTypes.includes(userIndustry)) {
    return false;
  }

  // 7. Feature flag — when set, the item is hidden only if the tenant's
  // resolved entitlements explicitly turn it OFF. Grandfathered tenants (no
  // entitlements map) are never hidden. Flags ride on user.entitlements.
  if (item.flag) {
    const ents = user?.entitlements;
    if (ents && ents[item.flag] === false) {
      return false;
    }
  }

  return true;
}


/**
 * Filter the full sidebar menu config down to the items visible
 * for the given user and workspace context.
 *
 * @param {Array}  menuConfig          — SIDEBAR_MENU from sidebarMenu.js
 * @param {Object} user                — user object from auth context
 * @param {string} [workspaceOverride] — optional manual workspace (for RoleSwitcher)
 * @returns {Array} filtered menu items in the same shape Sidebar.jsx expects
 */
export function getVisibleSidebar(menuConfig, user, workspaceOverride = null) {
  const workspace = workspaceOverride || resolveWorkspace(user);
  const isAdmin = isAdminUser(user);

  const filtered = menuConfig
    .filter((item) => isItemVisible(item, user, workspace, isAdmin))
    .map((item) => {
      // Top-level items without children pass through as-is
      if (!item.children) return item;

      // Filter children with same visibility rules
      const visibleChildren = item.children.filter((child) =>
        isItemVisible(child, user, workspace, isAdmin)
      );

      // If all children are hidden, drop the entire section
      if (visibleChildren.length === 0) return null;

      return { ...item, children: visibleChildren };
    })
    .filter(Boolean);

  // Apply workspace-specific section ordering if defined.
  // Unlisted workspaces keep the default array order.
  const sectionOrder = WORKSPACE_SECTION_ORDER[workspace];
  if (sectionOrder) {
    filtered.sort((a, b) => {
      const idxA = sectionOrder.indexOf(a.id);
      const idxB = sectionOrder.indexOf(b.id);
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });
  }

  return filtered;
}
